import React, { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";

/* icons */
import Multiple from "../assets/lyves_status/multiple.png";
import Invoiced from "../assets/lyves_status/status-I.png";
import Q from "../assets/lyves_status/status-q.png";
import L from "../assets/lyves_status/status-L.svg";
import M from "../assets/lyves_status/status-M.svg";
import N from "../assets/lyves_status/status-N.svg";
import R from "../assets/lyves_status/status-R.svg";
import S from "../assets/lyves_status/status-S.svg";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { useSelector } from "react-redux";

const MUI_WARNING_ICON = "__MUI_WARNING_ICON__";

const STATUS_MAP = {
  INVOICED: { icon: Invoiced, label: "Invoiced" },
  SCHEDULED: { icon: S, label: "Scheduled" },
  "LOAD LISTED": { icon: L, label: "Load Listed" },
  MANIFESTED: { icon: M, label: "Manifested" },
  "NOT SCHEDULED": { icon: N, label: "Not Scheduled" },
  RECORDED: { icon: R, label: "Recorded" },
  "QUOTE ORDER": { icon: Q, label: "Quote Order" },
  MULTIPLE: { icon: Multiple, label: "Multiple" },
  UNKNOWN: { icon: null, label: "Unknown" },
};

const FADE_MS = 180;
const OFFSET_Y = 10;

const formatTs = (ts) => {
  try {
    return new Date(ts).toLocaleString();
  } catch {
    return ts || "";
  }
};

function pickDefaultIcon(label) {
  const pool = [Multiple, Invoiced, L, M, N, R, S].filter(Boolean);
  if (!pool.length) return null;
  const s = String(label || "");
  let hash = 0;
  for (let i = 0; i < s.length; i++) {
    hash = (hash << 5) - hash + s.charCodeAt(i);
    hash |= 0;
  }
  const idx = Math.abs(hash) % pool.length;
  return pool[idx];
}

function humanizeLabel(raw) {
  if (raw == null) return "Unknown";
  const s = String(raw).replace(/[_-]+/g, " ").trim();
  if (!s) return "Unknown";
  return s
    .toLowerCase()
    .split(/\s+/)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

function getMetaForRaw(raw) {
  try {
    if (raw && typeof raw === "object" && (raw.icon || raw.label)) {
      return { icon: raw.icon ?? null, label: String(raw.label ?? "Unknown") };
    }
    const keyUpper = String(raw ?? "UNKNOWN").trim().toUpperCase();
    if (keyUpper in STATUS_MAP) return STATUS_MAP[keyUpper];
    return { icon: MUI_WARNING_ICON, label: humanizeLabel(raw) || "Unknown" };
  } catch (err) {
    return { icon: MUI_WARNING_ICON, label: "Unknown" };
  }
}

// Returns one (latest) status entry per order_number
function getLatestPerOrder(lyvesArr = []) {
  if (!Array.isArray(lyvesArr) || lyvesArr.length === 0) return [];
  const map = new Map();
  for (const s of lyvesArr) {
    const key = s.order_number || "__NO_ORDER__";
    const t = (() => {
      const d = new Date(s.timestamp);
      const tms = Number.isFinite(d.getTime()) ? d.getTime() : 0;
      return tms;
    })();
    const existing = map.get(key);
    if (!existing || t > existing.__ts) {
      map.set(key, { ...s, __ts: t });
    }
  }
  return Array.from(map.values()).map(({ __ts, ...rest }) => rest);
}

// Group full history by order_number, sort each group's entries by timestamp asc
function groupByOrder(lyvesArr = []) {
  const map = new Map();
  for (const s of lyvesArr || []) {
    const key = s.order_number || "__NO_ORDER__";
    if (!map.has(key)) map.set(key, []);
    map.get(key).push(s);
  }
  return Array.from(map.entries()).map(([orderNumber, entries]) => {
    entries.sort(
      (a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
    );
    const latest = entries.reduce((acc, e) => {
      return new Date(e.timestamp).getTime() > new Date(acc.timestamp).getTime()
        ? e
        : acc;
    }, entries[0]);
    return { orderNumber, entries, latest };
  });
}

function computePrimaryMetaFromLatest(latestArr = []) {
  if (!Array.isArray(latestArr) || latestArr.length === 0) {
    const picked = pickDefaultIcon(null);
    return { icon: picked || MUI_WARNING_ICON, label: "Unknown" };
  }
  if (latestArr.length > 1) {
    if (
      latestArr.every(
        (e) =>
          (e.despatch_status || e.dispatch_status || e.production_status || "").toUpperCase() ===
          "INVOICED"
      )
    ) {
      return STATUS_MAP.INVOICED;
    }
    return STATUS_MAP.MULTIPLE;
  }
  const single = latestArr[0] || {};
  const raw =
    single.despatch_status || single.dispatch_status || single.production_status || "UNKNOWN";
  return getMetaForRaw(raw);
}

function formatDeliveryDate(raw) {
  const str = String(raw ?? "");

  if (str.length !== 8) return "N/A";

  const year = str.slice(0, 4);
  const month = str.slice(4, 6);
  const day = str.slice(6, 8);

  return `${day}/${month}/${year}`;
}

const JobStatusTooltip = ({ row, table = false, dates = false }) => {
  const [open, setOpen] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [anchorRect, setAnchorRect] = useState(null);
  const [clamped, setClamped] = useState({ left: 0, top: 0 });
  const user = useSelector((state) => state.app.user);

  const admin = user?.Role >= 99;

  // ------ BBC detection (adjust to your real user object) ------
  const isBBC = (() => {
    if (!user) return false;
    // common fallbacks — change to match your user model if needed
    return Boolean(
      user.Role >= 90
    );
  })();

  const iconRef = useRef(null);
  const tooltipRef = useRef(null);
  const unmountTimer = useRef(null);

  // data helpers
  const fullHistory = row?.LyvesStatus || [];
  const latestPerOrder = getLatestPerOrder(fullHistory);
  const grouped = groupByOrder(fullHistory); // for admin view

  // primary meta should be based on latest-per-order view, not raw full history
  const primaryMeta =
    computePrimaryMetaFromLatest(latestPerOrder) || { icon: MUI_WARNING_ICON, label: "Unknown" };

  // Pick the latest delivery date across all orders (YYYYMMDD strings are lexicographically comparable)
  const computedLatestDeliveryRaw = (() => {
    if (!latestPerOrder.length) return null;
    const dates = latestPerOrder.map((o) => o.date_required).filter(Boolean);
    if (!dates.length) return null;
    return dates.reduce((max, curr) => (curr > max ? curr : max), dates[0]);
  })();

  const computedLatestDelivery = computedLatestDeliveryRaw
    ? formatDeliveryDate(computedLatestDeliveryRaw)
    : "N/A";

  // RULES:
  // - If primary status is INVOICED -> do NOT show delivery date at the top.
  // - If primary status is MULTIPLE -> show computedLatestDelivery.
  // - For non-BBC users: do not show icons except when primary is INVOICED.
  const primaryLabelUpper = String(primaryMeta.label || "UNKNOWN").trim().toUpperCase();
  const isInvoiced = primaryLabelUpper === "INVOICED";
  const isMultiple = primaryLabelUpper === "MULTIPLE";

  // non-BBC users get a simplified, non-hovering view unless they are invoiced (invoiced keeps icon/hover)
  const disableHoverAndIconsForNonBBC = !isBBC && !isInvoiced;

  // top delivery date to display: hide entirely when invoiced (per request)
  const topDeliveryToShow = isBBC ? (isInvoiced || table ? "" : computedLatestDelivery) : ""; // empty string hides

  // whether to render the primary icon:
  const shouldRenderIcon = isBBC ? Boolean(primaryMeta.icon) : isInvoiced;

  // --- hover logic: if disabled and not admin, don't attach hover handlers or show the tooltip
  const hoverEnabled = disableHoverAndIconsForNonBBC || admin;

  useEffect(() => {
    if (!hoverEnabled) return; // nothing to mount if hover disabled
    if (open) {
      if (unmountTimer.current) {
        clearTimeout(unmountTimer.current);
        unmountTimer.current = null;
      }
      setMounted(true);
      if (iconRef.current) {
        setAnchorRect(iconRef.current.getBoundingClientRect());
      }
    } else {
      unmountTimer.current = setTimeout(() => {
        setMounted(false);
        unmountTimer.current = null;
      }, FADE_MS + 20);
    }
    return () => {
      if (unmountTimer.current) {
        clearTimeout(unmountTimer.current);
        unmountTimer.current = null;
      }
    };
  }, [open, hoverEnabled]);

  useEffect(() => {
    if (!hoverEnabled || !open) return;
    const updateRect = () => {
      if (iconRef.current) setAnchorRect(iconRef.current.getBoundingClientRect());
    };
    window.addEventListener("scroll", updateRect, true);
    window.addEventListener("resize", updateRect);
    return () => {
      window.removeEventListener("scroll", updateRect, true);
      window.removeEventListener("resize", updateRect);
    };
  }, [open, hoverEnabled]);

  useEffect(() => {
    if (!mounted || !anchorRect) return;
    requestAnimationFrame(() => {
      const tip = tooltipRef.current;
      const tipRect = tip ? tip.getBoundingClientRect() : { width: 0, height: 0 };
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      const margin = 8;

      let left = anchorRect.left + anchorRect.width / 2 - tipRect.width / 2;
      let top = anchorRect.top - tipRect.height - OFFSET_Y;

      if (top < margin) {
        top = anchorRect.bottom + OFFSET_Y;
      }

      if (left < margin) left = margin;
      if (left + tipRect.width + margin > vw) left = Math.max(margin, vw - tipRect.width - margin);

      if (top + tipRect.height + margin > vh) top = Math.max(margin, vh - tipRect.height - margin);
      if (top < margin) top = margin;

      setClamped({ left, top });
    });
  }, [mounted, anchorRect]);

  useEffect(() => {
    return () => {
      if (unmountTimer.current) clearTimeout(unmountTimer.current);
    };
  }, []);

  if (dates && topDeliveryToShow) return topDeliveryToShow; // Dont show for non BBC users. 

  if (!row?.LyvesStatus?.length) return null;

  const isPng = primaryMeta?.icon?.toLowerCase().endsWith(".png");

  const iconStyle = {
    width: (isPng ? 45 : 50) - (table ? 5 : 0),
    position: table ? "relative" : "absolute",
    left: isPng ? (table ? 3 : 5) : 0,
    bottom: table ? 0 : (isPng ? 50 : 45),
    cursor: "pointer",
    transform: "translateX(2px)",
    display: "block",
    zIndex: 5,
  };

  const tooltipBase = {
    position: "fixed",
    left: clamped.left,
    top: clamped.top,
    background: "#fff",
    border: "1px solid #ddd",
    borderRadius: 8,
    boxShadow: "0 6px 18px rgba(0,0,0,0.12)",
    padding: 8,
    minWidth: 360,
    maxWidth: "80vw",
    zIndex: 99999,
    maxHeight: "60vh",
    overflowY: "auto",
    pointerEvents: open ? "auto" : "none",
    transition: `opacity ${FADE_MS}ms ease, transform ${FADE_MS}ms ease`,
    opacity: open ? 1 : 0,
    transform: open ? "translateY(0) scale(1)" : "translateY(-6px) scale(0.99)",
    transformOrigin: "top center",
  };

  const tableStyle = {
    width: "100%",
    borderCollapse: "collapse",
    fontSize: 13,
  };

  // If hover is disabled for this user (non-BBC and not invoiced), render simple static delivery date only.
  if (!hoverEnabled && !shouldRenderIcon) {
    // topDeliveryToShow is empty when invoiced (we already hide), otherwise shows latest delivery or "N/A".
    return (
      <div style={{ display: "inline-block", position: "relative" }}>
        {topDeliveryToShow ? (
          <p
            style={{
              width: 80,
              height: 46,
              color: "#6097e1",
              fontWeight: 600,
              cursor: "default",
              position: "absolute",
              fontSize: "smaller",
              left: 58,
              bottom: 28,
              zIndex: 5,
            }}
          >
            Delivery Date {topDeliveryToShow}
          </p>
        ) : null}
      </div>
    );
  }

  // Otherwise render the original interactive version (icons possibly hidden depending on shouldRenderIcon)
  return (
    <>
      <div
        style={{ display: "inline-block" }}
        {...(hoverEnabled
          ? {
            onMouseEnter: () => {
              if (iconRef.current) setAnchorRect(iconRef.current.getBoundingClientRect());
              setOpen(true);
            },
            onMouseLeave: () => setOpen(false),
          }
          : {})}
      >
        {topDeliveryToShow ? (
          <p
            style={{
              width: 80,
              height: 46,
              color: "#6097e1",
              fontWeight: 600,
              cursor: "default",
              position: table ? "relative" : "absolute",
              fontSize: "smaller",
              left: admin ? 55 : 20,
              bottom: 28,
              zIndex: 5,
            }}
          >
            Delivery Date {topDeliveryToShow}
          </p>
        ) : null}

        {shouldRenderIcon ? (
          primaryMeta?.icon === MUI_WARNING_ICON ? (
            <WarningAmberIcon
              ref={iconRef}
              sx={{
                width: 46,
                height: 46,
                color: "#ffb300",
                cursor: hoverEnabled ? "pointer" : "default",
                position: table ? "relative" : "absolute",
                left: 3,
                bottom: table ? 0 : 45,
                zIndex: 5,
              }}
            />
          ) : primaryMeta?.icon ? (
            <img
              ref={iconRef}
              src={primaryMeta.icon}
              alt={primaryMeta.label}
              style={iconStyle}
              onLoad={() => {
                if (open && iconRef.current) setAnchorRect(iconRef.current.getBoundingClientRect());
              }}
            />
          ) : (
            <div
              ref={iconRef}
              style={{
                ...iconStyle,
                width: 46,
                height: 46,
                borderRadius: 46,
                background: "#999",
                color: "#fff",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontWeight: 600,
              }}
            >
              {primaryMeta.label ? primaryMeta.label.charAt(0) : "?"}
            </div>
          )
        ) : !isInvoiced && table && isBBC && <p
          style={{
            width: 80,
            height: 46,
            color: "#6097e1",
            fontWeight: 600,
            fontSize: "smaller",
            zIndex: 5,
          }}
        >
          Delivery Date {topDeliveryToShow}
        </p>}
      </div>

      {mounted && admin &&
        createPortal(
          <div
            ref={tooltipRef}
            style={tooltipBase}
            onMouseEnter={() => setOpen(true)}
            onMouseLeave={() => setOpen(false)}
            role="dialog"
            aria-label="Lyves status history"
          >
            <div style={{ marginBottom: 6, fontWeight: 600 }}>
              {`Lyves Status history (${fullHistory.length}) - Delivery Date ${topDeliveryToShow || ""}`}
            </div>
            <div>
              {grouped.map((grp, idx) => {
                const groupMeta = getMetaForRaw(
                  grp.latest.despatch_status ||
                  grp.latest.dispatch_status ||
                  grp.latest.production_status ||
                  "UNKNOWN"
                );
                const orderLabel =
                  grp.orderNumber === "__NO_ORDER__"
                    ? ""
                    : `${grp.orderNumber} - Delivery Date: ${formatDeliveryDate(grp.latest.date_required)}`;

                return (
                  <div
                    key={idx}
                    style={{
                      marginBottom: 10,
                      paddingBottom: 6,
                      borderBottom: "1px solid #f2f2f2",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        marginBottom: 6,
                      }}
                    >
                      {groupMeta.icon === MUI_WARNING_ICON ? (
                        <WarningAmberIcon style={{ width: 18, height: 18, color: "#ffb300" }} />
                      ) : groupMeta.icon ? (
                        <img src={groupMeta.icon} alt={groupMeta.label} style={{ width: 18, height: 18 }} />
                      ) : (
                        <div
                          style={{
                            width: 18,
                            height: 18,
                            borderRadius: 18,
                            background: "#ddd",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          {groupMeta.label?.charAt(0)}
                        </div>
                      )}
                      <div style={{ fontWeight: 600 }}>{orderLabel || "(no order)"}</div>
                      <div style={{ marginLeft: "auto", color: "#666", fontSize: 12 }}>
                        {grp.entries.length} update{grp.entries.length > 1 ? "s" : ""}
                      </div>
                    </div>

                    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                      <div style={{ position: "relative", paddingLeft: 14 }}>
                        <div
                          style={{
                            position: "absolute",
                            left: 27,
                            top: 8,
                            bottom: 8,
                            width: 2,
                            background: "#c9c9c9",
                            zIndex: 0,
                          }}
                        />

                        {grp.entries.map((s, i2) => {
                          const raw =
                            s.despatch_status || s.dispatch_status || s.production_status || "UNKNOWN";
                          const meta = getMetaForRaw(raw);
                          const isLast = i2 === grp.entries.length - 1;

                          return (
                            <div
                              key={i2}
                              style={{
                                display: "flex",
                                gap: 8,
                                alignItems: "flex-start",
                                position: "relative",
                                zIndex: 1, // dots above the line
                              }}
                            >
                              {/* timeline column */}
                              <div
                                style={{
                                  width: 28,
                                  display: "flex",
                                  flexDirection: "column",
                                  alignItems: "center",
                                }}
                              >
                                {/* dot */}
                                <div
                                  style={{
                                    width: 8,
                                    height: 8,
                                    borderRadius: 8,
                                    background: isLast ? "#008200" : "#ccc",
                                    marginTop: 6,
                                  }}
                                />
                              </div>

                              {/* content */}
                              <div style={{ flex: 1 }}>
                                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                  {meta.icon === MUI_WARNING_ICON ? (
                                    <WarningAmberIcon style={{ width: 14, height: 14, color: "#ffb300" }} />
                                  ) : meta.icon ? (
                                    <img src={meta.icon} alt={meta.label} style={{ width: 14, height: 14 }} />
                                  ) : (
                                    <div style={{ minWidth: 14 }}>{meta.label?.charAt(0)}</div>
                                  )}
                                  <div style={{ fontWeight: isLast ? 500 : 200 }}>{meta.label}</div>
                                  <div style={{ marginLeft: "auto", color: "#666", fontSize: 12 }}>
                                    {formatTs(s.timestamp)}
                                  </div>
                                </div>

                                {s.note && (
                                  <div style={{ fontSize: 12, color: "#444", marginTop: 4 }}>{s.note}</div>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>,
          typeof document !== "undefined" ? document.body : null
        )}
    </>
  );
};

export default JobStatusTooltip;
